import { createClient } from '@supabase/supabase-js';
import { getOrCreateClientSessionId } from '../services/imageProcessing';

// Helper function to get checkout session ID from the URL, handling encoding issues
function getCheckoutSessionIdFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  
  // Get session_id from URL if available
  const urlParams = new URLSearchParams(window.location.search);
  let sessionId = urlParams.get('session_id');
  
  // Clean up the session ID to handle encoding issues
  if (!sessionId) {
    // Try to extract from raw URL if URLSearchParams doesn't work
    const match = window.location.search.match(/[?&]session_id=([^&]+)/i);
    sessionId = match ? match[1] : null;
  }
  
  if (sessionId) {
    // Remove any URL encoding artifacts
    return sessionId.replace(/[^a-zA-Z0-9_]/g, '');
  }
  
  return null;
}

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Get initial session IDs
const initialClientSessionId = getOrCreateClientSessionId();
const initialCheckoutSessionId = getCheckoutSessionIdFromUrl();

// Create Supabase client with global headers for RLS policies
export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: { 
    autoRefreshToken: true,
    persistSession: true
  },
  global: {
    headers: {
      'client-session-id': initialClientSessionId,
      ...(initialCheckoutSessionId ? { 'x-checkout-session': initialCheckoutSessionId } : {})
    }
  }
});

// Function to refresh headers with the latest client_session_id
export const refreshClientSessionId = () => {
  const clientSessionId = getOrCreateClientSessionId();
  const checkoutSessionId = getCheckoutSessionIdFromUrl() || null;
  
  // Update global headers for all future requests
  const newHeaders = {
    'client-session-id': clientSessionId,
    ...(checkoutSessionId ? { 'x-checkout-session': checkoutSessionId } : {})
  };
  
  // Set the global headers on the supabase client
  if (supabase?.rest) {
    supabase.rest.headers = {
      ...supabase.rest.headers,
      ...newHeaders
    };
  }
  
  // Also set on the main client headers for compatibility
  if (supabase) {
    supabase.headers = {
      ...supabase.headers,
      ...newHeaders
    };
  }
};
    
// Initialize with current session IDs
refreshClientSessionId();

// Initialize headers and JWT claims